import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react"

import { useLocation } from "react-router-dom"

import type { Document } from "@/api/models"

import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import {
  CitationPreviewContext,
  type CitationPreviewContextValue,
  type CitationPreviewState,
} from "./citation-preview-shared"
import { openCitationPreviewPanel } from "./citation-preview-sidebar"

export function CitationPreviewProvider({ children }: { children: ReactNode }) {
  const [currentCitation, setCurrentCitation] = useState<CitationPreviewState | null>(null)
  const location = useLocation()

  const openPreview = useCallback(
    (
      citation: string,
      citationSection: string,
      options?: {
        chunkText?: string
        documents?: [string, Document][]
        backLabel?: string
        preserveSidebarStack?: boolean
      }
    ) => {
      const nextCitation = openCitationPreviewPanel({
        citation,
        citationSection,
        options,
        onClose: () => {
          setCurrentCitation(null)
        },
      })
      setCurrentCitation(nextCitation)
    },
    []
  )

  const closePreview = useCallback(() => {
    const sidebarState = useContentSidebarStore.getState()
    if (sidebarState.currentPanel?.id === "citation-preview") {
      sidebarState.closePanel()
    }
    setCurrentCitation(null)
  }, [])

  useEffect(() => {
    closePreview()
  }, [closePreview, location.pathname, location.search, location.hash])

  const value = useMemo<CitationPreviewContextValue>(
    () => ({
      currentCitation,
      isPreviewOpen: currentCitation !== null,
      openPreview,
      closePreview,
    }),
    [currentCitation, openPreview, closePreview]
  )

  return <CitationPreviewContext.Provider value={value}>{children}</CitationPreviewContext.Provider>
}
